import PropTypes from 'prop-types';

import gsiIcon from 'images/icons/gsi.svg';

import AlternateDrugTag from 'components/Tag/AlternateDrug';
import DosingInfoTag from 'components/Tag/DosingInfo';
import OtherGuidanceTag from 'components/Tag/OtherGuidance';
import {FONTAWESOME_FOCUS_ICON} from 'components/icons/Focus';
import DownloadLink from 'components/links/Download';
import Link from 'components/links/Link';
import OutreachTemplate from 'components/templates/Outreach';
import useAppContext from 'conf/AppContext';

const article = {
  type: 'Read',
  text: 'An Evidence-Based Framework for Evaluating Pharmacogenomics Knowledge for Personalized Medicine',
  by: <>Whirl-Carrillo, Huddart <i>et al</i>.</>,
  url: 'https://pubmed.ncbi.nlm.nih.gov/34216021/',
};

const _data = {
  canCollapse: false,
  initialIsCollapsed: false,
  sections: [
    {
      name: 'annotatedGuidelines',
      title: 'Annotated Clinical Guidelines',
      text: (
        <p>
          Clinical pharmacogenomics guidelines inform clinicians how to use a person&apos;s genetic information
          to help select a medication or adjust the dose of a medication.
          ClinPGx provides annotations of clinical guidelines written by
          the <Link newTab={true} href="/page/cpic">Clinical Pharmacogenetic Implementation Consortium (CPIC)</Link>,
          the <Link newTab={true} href="/page/dpwg">Dutch Pharmacogenetics Working Group (DPWG)</Link>, the
          Canadian Pharmacogenomics Network for Drug Safety (CPNDS), the French National Network of
          Pharmacogenetics (RNPGx), and other professional organizations. The CPIC guidelines are part of the ClinPGx
          resource.
        </p>
      ),
      image: 'guidelineAnnotation',
      sections: [
        {
          text: (
            <>
              <p>
                Annotations include a synopsis of the guideline and
                {' '}
                <Link href="/page/clinicalGuidelineLegend#clinical-guideline-annotation-tags" newTab={true}>tags</Link>
                {' '}
                to indicate the type of recommendation, whether it be to change the dose, use another drug, or another
                action such as increased monitoring:
                {' '}
                <AlternateDrugTag context="guidelineAnnotation" className="tag--xs" />,
                <DosingInfoTag context="guidelineAnnotation" className="tag--xs" /> and
                <OtherGuidanceTag context="guidelineAnnotation" className="tag--xs" />{' '}. Some guidelines may
                evaluate drug response based on genetic variation and conclude that there is no reason to change how
                that medication is prescribed. In that case, ClinPGx will indicate that the guideline provides &quot;No
                recommendation&quot; for that gene-drug pair. We include standardized gene symbols and generic drug
                names to allow for easy comparison of guidance from different guideline sources.
              </p>
              <p>
                For CPIC and DPWG guidelines, we have made connections across variants, haplotypes, diplotypes, gene
                phenotypes and recommendations for users, applying each group&apos;s definitions, which may vary
                slightly. We provide an interactive feature that takes a genotype or gene phenotype (e.g. poor
                metabolizer) of interest and displays the corresponding recommendation. We&apos;ve also created short
                video summaries of many CPIC guidelines.
              </p>
              <p>
                <b>How to find annotated clinical guidelines:</b>
                <ul>
                  <li>
                    See all <Link href="/guidelineAnnotations" newTab={true}>guideline annotations</Link> by
                    clicking on the &quot;Guidelines&quot; link under the &quot;Curated PGx Evidence&quot; section on the <Link href="/" newTab={true}>homepage</Link>.
                  </li>
                  <li>
                    Find all guideline annotations for a particular drug, gene, or genetic variation by entering the
                    name in the search box, navigating to the corresponding page, then clicking the &quot;Prescribing
                    Info&quot; tab found in the left-hand menu.
                  </li>
                  <li>
                    See which guideline annotations have guidance for a particular genotype or gene phenotype by
                    clicking on the
                    {' '}
                    <Link href="https://pharmdog.clinpgx.org" newTab={true}>PharmDOG</Link> or
                    {' '}
                    <Link href="/genotype" newTab={true}>GSI</Link> link under the &quot;Precision
                    Guidance&quot; section on the <Link href="/" newTab={true}>homepage</Link>. Read more about these
                    tools by clicking on the &quot;Precision Guidance Tools&quot; in the left-hand menu on this page.
                  </li>
                </ul>
              </p>
            </>
          ),
        },
      ],
    },
    {
      name: 'annotatedLabels',
      title: 'Annotated Drug Labels',
      text: (
        <p>
          Some medication labels discuss the role of genes and gene products
          on the metabolism or side effects of the drug. Sometimes, genetic variation is either an indication
          or contraindication for the medication. Rarely, labels provide dosing information based on a
          patient&apos;s genetics or gene phenotype. ClinPGx provides annotations of labels from the U.S., Canada,
          Europe, Switzerland and Japan that contain pharmacogenomic information.
        </p>
      ),
      image: 'labelAnnotation',
      sections: [
        {
          text: (
            <>
              <p>
                Annotations include a synopsis and quotes of the pharmacogenomics information found on the label. We
                assign a  <Link href="/page/drugLabelLegend#pgx-level" newTab={true}>level of PGx</Link>
                {' '}
                to inform people about the amount and type of pharmacogenomics information on the label, including
                whether testing a patient for a genetic change before giving them the medication is required or
                recommended. Labels for some medication contain advice about how to adjust dose based on genetic
                information, or information on the risk of adverse effects in people with certain genetic variants, or a
                discussion of the proteins involved in breaking down the drug in the body. When applicable, annotations
                are <Link href="/page/drugLabelLegend#drug-label-annotation-tags" newTab={true}>tagged</Link> to
                indicate the type of PGx guidance on the label: <AlternateDrugTag context="labelAnnotation" className="tag--xs" />,
                <DosingInfoTag context="labelAnnotation" className="tag--xs" />,
                {' '}
                <OtherGuidanceTag context="labelAnnotation" className="tag--xs" />{' '}.
                We include standardized gene symbols and generic drug names to allow for easy comparison of information
                from different label sources.
              </p>
              <p>
                FDA labels typically do not provide any information connecting genetic variation to gene phenotypes.
                We use the same connections across variants, haplotypes, diplotypes and gene phenotypes that we use for
                CPIC in order to provide users an interactive feature that takes a genotype of interest and displays the
                corresponding label. FDA annotations also have a section highlighting the
                {' '}
                <Link href="/fdaPgxAssociations" newTab={true}>FDA Table of Pharmacogenetic Associations</Link>
                {' '}
                entry, if there is one for that medication.
              </p>
              <p>
                <b>How to find annotated drug labels:</b>
                <ul>
                  <li>
                    See all <Link href="/labelAnnotations" newTab={true}>drug label annotations </Link> by
                    clicking on the &quot;Drug Labels&quot; link under the &quot;Curated PGx Evidence&quot; section on the <Link href="/" newTab={true}>homepage</Link>.
                  </li>
                  <li>
                    See all <Link href="/fdaLabelAnnotations" newTab={true}>FDA drug label annotations</Link> by
                    clicking on the &quot;FDA Labels&quot; link under the &quot;Curated PGx Evidence&quot; section on the <Link href="/" newTab={true}>homepage</Link>.
                  </li>
                  <li>
                    Find all label annotations for a particular drug, or regarding a particular gene, by entering the
                    drug or gene in the search box, navigating to the corresponding page, then clicking the &quot;Drug
                    Label Annotations&quot; tab found in the left-hand menu.
                  </li>
                  <li>
                    See which label annotations have guidance for a particular genotype or gene phenotype by clicking on
                    the <Link href="https://pharmdog.clinpgx.org" newTab={true}>PharmDOG</Link> or
                    <Link href="/genotype" newTab={true}>GSI</Link> link under the &quot;Precision
                    Guidance&quot; section on the <Link href="/" newTab={true}>homepage</Link>. Read more about these
                    tools by clicking on the &quot;Precision Guidance Tools&quot; in the left-hand menu on this page.
                  </li>
                </ul>
              </p>
            </>
          ),
        },
      ],
    },
    {
      name: 'annotatedLiterature',
      title: 'Annotated Literature',
      text: (
        <p>
          Researchers and clinicians typically publish experimental and clinical study findings in peer-reviewed
          scientific journals. The ClinPGx team searches published literature using manual and automated methods to
          identify, curate and annotate pharmacogenomic publications.
        </p>
      ),
      image: 'clinicalAnnotation',
      sections: [
        {
          text: (
            <>
              <p>
                <b>Variant annotations</b>
              </p>
              <p>
                A variant annotation is an overview of the association between a variant, haplotype or gene phenotype
                and the drug response that is reported in a single publication. Articles may show that a particular
                genetic change affects the likelihood that patients have an adverse reaction, or instead respond well,
                to a medication. Or a publication may show that a particular genetic change alters the activity of a
                drug metabolizing enzyme. Our scientists capture that information, along with other details such as
                study size, study type and statistical significance of the association. Each variant annotation is
                categorized by one or more association type: efficacy, toxicity, metabolism/pharmacokinetics
                (PK), dosage, pharmacodynamics (PD) or other. We apply a published algorithm to assess the information
                and assign a score to the annotation.
              </p>
              <p>
                <b>How to find variant annotations:</b>
                <ul>
                  <li>
                    Find all variant annotations for a particular drug, gene or genetic variation by entering the
                    name in the search box, navigating to the corresponding page, then clicking the &quot;Variant
                    Annotations&quot; tab found in the left-hand menu.
                  </li>
                </ul>
              </p>
              <p>
                <b>Summary annotations</b>
              </p>
              <p>
                A summary annotation (previously referred to as a clinical annotation) summarizes the relationship
                across all variant annotations for a genetic variant and a medication response. The annotation is scored
                based on the sum of the supporting variant annotation scores, and supporting CPIC, DPWG and FDA
                annotations. The summary annotation score determines the Level of Evidence (LOE) assignment, indicating
                the amount and quality of the supporting evidence. As new variant annotations from published studies are
                added, the score is recalculated, which can potentially lead to a change in the LOE. All summary
                annotations with a LOE of 1A-2B are independently reviewed by two ClinPGx scientists.
              </p>
              <p>
                <b>How to find summary annotations:</b>
                <ul>
                  <li>
                    See all <Link href="/summaryAnnotations" newTab={true}>summary annotations</Link> by LOE group by
                    clicking on the &quot;Literature&quot; link under the &quot;Curated PGx Evidence&quot; section on
                    the <Link href="/" newTab={true}>homepage</Link>.
                  </li>
                  <li>
                    Find all summary annotations for a particular drug, gene or genetic variation by entering the
                    name in the search box, navigating to the corresponding page, then clicking the &quot;Summary
                    Annotations&quot; tab found in the left-hand menu.
                  </li>
                </ul>
              </p>
            </>
          ),
        },
        {
          interested: [
            article,
          ],
        },
      ],
    },
    {
      name: 'pathways',
      title: 'Curated Pathways',
      text: (
        <p>
          Curated pathways are evidence-based diagrams that illustrate how a medication is metabolized in the body, how
          a medication works in the body, or both. Pathway diagrams are also accompanied by a written description.
        </p>
      ),
      image: 'pathway',
      sections: [
        {
          text: (
            <>
              <p>
                ClinPGx pathways focus on the
                {' '}<Link newTab={true} href="/page/glossary#pharmacokinetics">pharmacokinetics</Link> and/or
                {' '}<Link newTab={true} href="/page/glossary#pharmacodynamics">pharmacodynamics</Link> of
                a drug, and depict the key candidate pharmacogenes that code for the proteins involved in these
                processes. Most pathways are interactive, allowing users to access more information by clicking on
                the objects in the pathway. &quot;In progress&quot; pathways provide the preliminary underlying
                reactions, genes, metabolites and evidence in a machine readable format but have not yet been fully
                developed.
              </p>
              <p>
                Connecting arrows on a pathway are supported by literature evidence, which can be viewed on the pathway
                page. Pathways are typically created in collaboration with experts in the field.
                They are available for download in multiple formats, including PDF files, and are often
                published in the journal <i>Pharmacogenetics and Genomics</i>.
              </p>
              <p>
                <b>How to find pathways:</b>
                <ul>
                  <li>See all <Link href="/pathways" newTab={true}>pathways</Link> by clicking on the
                    &quot;Pathways&quot; link under the &quot;Curated PGx Evidence&quot; section on the <Link href="/" newTab={true}>homepage</Link>.
                  </li>
                  <li>Find all pathways for a particular drug, or that involve a particular gene, by entering the drug
                    or gene in the search box, navigating to the corresponding page, then clicking the
                    &quot;Pathways&quot; tab found in the left-hand menu.
                  </li>
                </ul>
              </p>
            </>
          ),
        },
      ],
    },
    {
      name: 'tools',
      title: 'Precision Guidance Tools',
      text: (
        <p>
          The Pharmacogenomics Drug Optimization Guidance Tool (PharmDOG) and the Genotype Selection Interface (GSI)
          enable users to access drug prescribing recommendations by entering genetic test results through a web form.
          PharmCAT (Pharmacogenomics Clinical Annotation Tool) is a software tool that takes genetic variants presented
          in a specific file format (VCF), traverses all the connections necessary to determine gene phenotypes, and
          then presents the corresponding guidance.
        </p>
      ),
      image: gsiIcon,
      sections: [
        {
          text: (
            <>
              <p>
                ClinPGx curates genotype-based prescribing guidance from clinical guidelines and regulatory
                agency-approved drug labels. ClinPGx tools providing pharmacogenomic prescribing guidance -  PharmDOG
                (previously referred to as DDRx), GSI and PharmCAT - use structured clinical guideline and drug label
                annotations from the following
                sources: the <Link href="/page/cpic" newTab={true}>Clinical Pharmacogenetics Implementation Consortium (CPIC)</Link>
                the <Link href="/page/dpwg" newTab={true}>Dutch Pharmacogenetics Working Group (DPWG)</Link>,
                and <Link href="/page/fda" newTab={true}>FDA</Link>&apos;s approved drug labels and Table of
                Pharmacogenetic Associations.
              </p>
              <p>
                <Link href="https://pharmdog.clinpgx.org/" newTab={true}>PharmDOG</Link> focuses on guidance that calls
                for a change in dose or of medication (i.e. actionable guidance). The tool allows users choose which
                guidance sources are displayed and how they are prioritized.
                It is designed to work equally well on computers and mobile devices. In addition to searching across all
                medications based on genetic test results, this tool has the option to search by medication first to see
                what genes are relevant.
              </p>
              <p>
                <Link href="/genotype" newTab={true}>GSI</Link> enables users to compare across guidance sources in a
                side-by-side display. It shows all results,
                including those that recommend staying with standard of care (i.e. no dosing or medication change) and
                indicates which gene-drug pairs may have been evaluated and determined not to be actionable. GSI is for
                the user that is interested in surveying guidance from all sources at once in order to inform decisions,
                which requires more screen space and is not well-suited to mobile devices.
              </p>
              <p>
                Both tools can generate a printable report with a QR code that you can save and share. Subsequent use of
                the QR code ensures access to the most up-to-date guidance. Both tools also offer a feature called
                CYP2D6 phenoconversion. When entering a genetic test result for CYP2D6, users can choose concomitant
                medications that may affect the CYP2D6 phenotype to receive PGx guidance based on the adjusted phenotype
                (i.e. phenoconversion).
              </p>
              <p>
                <Link href="https://pharmcat.clinpgx.org/" newTab={true}>PharmCAT</Link> is a standalone software tool
                that can interpret DNA sequencing (genome or exome, phased or
                unphased) or genotyping test results from VCF files. This bioinformatics tool requires users to have
                some familiarity with running command line code. It produces an HTML report of all haplotypes, star
                alleles, diplotypes, gene phenotypes and guideline recommendations and FDA wording.  It also produces
                structured JSON output for computational analysis of results which can be imported into other systems
                such as diagnostic laboratory Genetic Information Management Systems (GIMS) and Electronic Health
                Records (EHR). PharmCAT can also be used as a research tool to generate star allele frequencies from
                biobank datasets.
              </p>
              <p>
                <b>How to find precision guidance tools:</b>
                <ul>
                  <li>
                    Tools are listed under the &quot;Precision Guidance&quot; section on the <Link href="/" newTab={true}>homepage</Link>.
                  </li>
                </ul>
              </p>
            </>
          ),
        },
        {
          interested: [
            {
              type: 'Read',
              text: 'More about PharmDOG',
              url: 'https://pharmdog.clinpgx.org/documentation',
            },
            {
              type: 'Read',
              text: 'More about the GSI',
              url: '/page/gsiDocumentation',
            },
            {
              type: 'Read',
              text: 'More about PharmCAT',
              url: 'https://pharmcat.clinpgx.org/methods/',
            },
          ],
        },
      ],
    },
    {
      name: 'focus',
      title: 'Focus',
      text: (
        <p>
          ClinPGx&apos;s focus feature allows you to select an area of interest and tailor how you see
          the data on the site. Selecting a focus changes the display so that annotations relevant
          to that area are pulled out and prominently displayed. Our first focus area is pediatrics.
        </p>
      ),
      // intentionally using fal to match icons on this page
      image: ['fal', FONTAWESOME_FOCUS_ICON],
      sections: [
        {
          text: (
            <>
              <p>
                The pediatric focus feature moves annotations which
                include pediatric populations to the top of the page, making them easier to find.
                On gene, drug or genetic variation pages, a child icon will appear on the tabs
                found in the left-hand menu if annotations include pediatric information.
                We also offer supplementary content with our pediatric dashboard and pediatric
                summaries for over 200 drugs.
              </p>
              <p>
                <b>How to find the pediatric focus feature:</b>
                <ul>
                  <li>
                    The switch can be found under the binoculars icon in the top right corner of every page on the site.
                  </li>
                  <li>
                    The switch is also prominently displayed under the &quot;Pediatrics&quot; section on the
                    <Link href="/" newTab={true}>homepage</Link>.
                  </li>
                </ul>
              </p>
            </>
          ),
        },
        {
          interested: [
            {
              type: 'Read',
              text: 'Intro to Focus',
              url: '/page/focusIntro',
            },
            {
              type: 'Read',
              text: 'Pediatric pharmacogenomics',
              url: '/page/PedsIntro',
            },
            {
              text: 'Access the pediatric dashboard',
              url: '/pediatric/dashboard',
            },
          ],
        },
      ],
    },
    {
      name: 'resources',
      title: 'Resources',
      text: (
        <>
          <p>
            CPIC resources are found under the &quot;CPIC&quot; heading on the <Link href="/" newTab={true}>homepage</Link>:
          </p>
          <p>
            <ul>
              <li>
                <Link href="/cpic/guidelines" newTab={true}>Guidelines</Link>: a list of CPIC guidelines with links to
                guideline manuscripts and supplements.
              </li>
              <li>
                <Link href="/page/pgxGeneRef" newTab={true}>Gene tables</Link>: CPIC guideline gene tables, including
                allele definition, functionality, diplotype-phenotype and frequency tables, example CDS alert language
                and workflows.
              </li>
              <li>
                <Link href="https://github.com/cpicpgx/cpic-data" newTab={true}>CPIC DB & API</Link>: a link to the
                CPIC Github repo leading to the database and API.
              </li>
            </ul>
          </p>
          <p>
            Other pharmacogenomic resources can be found under the &quot;Knowledge&quot;, &quot;Implementation&quot; and
            &quot;Education&quot; headings on the <Link href="/" newTab={true}>homepage</Link>:
          </p>
          <p>
            <ul>
              <li><Link href="/page/cancerPgx" newTab={true}>Cancer Pharmacogenetics</Link>: collection of genes, drugs and pathways with pharmacogenetic information relevant to cancer.</li>
              <li><Link href="/page/DrugUseAndAddiction" newTab={true}>PGx in Drug Use and Addiction</Link>: collection of genes, drugs and pathways with pharmacogenetic information relevant to addiction.</li>
              <li><Link href="/alleleFrequency" newTab={true}>Allele frequencies</Link>: for star alleles, haplotypes and variants, from CPIC and biobank populations.</li>
              <li><Link href="/ampAllelesToTest" newTab={true}>AMP Recommendations</Link>: list of guidelines from the Association for Molecular Pathology (AMP PGx Working Group) summarizing &quot;must-test&quot; alleles and variants to promote testing standardization across clinical laboratories.</li>
              <li><Link href="/alleleFunctions" newTab={true}>Allele function assignments</Link>: from CPIC and DPWG.</li>
              <li><Link href="/fdaPgxAssociations" newTab={true}>FDA PGx Associations</Link>: searchable, sortable list of the information contained in the FDA Table of Pharmacogenetic Associations.</li>
              <li><Link href="/page/biogeographicalGroups" newTab={true}>Biogeographical Groups for PGx</Link>: explanatory information about the ClinPGx biogeographical grouping system.</li>
              <li><Link href="/page/educationalResources" newTab={true}>Educational Resources</Link>: links to educational resources in pharmacogenomics provided by ClinPGx and external organizations.</li>
              <li><Link href="/page/curationTips" newTab={true}>PGx Publication Tips</Link>: advice on how to write scientific publications to ensure they can be easily curated into ClinPGx.</li>
            </ul>
          </p>
        </>
      ),
      // intentionally using fal to match icons on this page
      image: ['fal', 'circle-info'],
      sections: [],
    },
  ],
};

const propTypes = {
  baseUrl: PropTypes.string.isRequired,
  section: PropTypes.string,
};
export default function ExploreClinpgxPage({baseUrl, section}) {
  const appContext = useAppContext();
  return (
    <OutreachTemplate
      title="Explore ClinPGx"
      subtitle={(
        <div>
          <p>ClinPGx is an NIH-funded resource that provides information about how human genetic variation affects response to medications. We catalog, curate, generate and disseminate knowledge about clinically actionable gene-drug associations and genotype-phenotype relationships. The sections below present the different types of pharmacogenomic information you can find here.</p>
        </div>
      )}
      alert={(
        <>
          <p>
            Read about the <Link href="/page/overview">project purpose, background and team</Link>. Learn about
            {' '}
            <Link href="/page/searchingClinpgx">our search capabilities</Link>. See <Link href="/page/vocabulary">our technical definitions</Link> for genetic variation.
          </p>
          <p>
            ClinPGx has <strong>training exercises</strong> available to help new users find their way around this
            website. Note that these exercises are not to be used for credit, including professional development such
            as CME.
            <DownloadLink className="ml-2" href={appContext.downloadAttachmentUrl('PharmGKB_Training_Exercises.zip')}>Download training exercises</DownloadLink>
          </p>

        </>
      )}
      data={_data}
      section={section}
      baseUrl={baseUrl}
    />
  );
}
ExploreClinpgxPage.propTypes = propTypes;
